import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

const MENU_ITEM_IDS = ['about', 'experience', 'projects', 'skills', 'education', 'contact'];

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', ...MENU_ITEM_IDS];
      const currentSection = sections.find((section) => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when clicking on a link
  const handleMenuClick = () => {
    setIsMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('nav')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <header 
      className={`fixed w-full max-w-full z-50 transition-all duration-300 ${
        scrolled ? 'shadow-lg shadow-secondary/10' : ''
      }`}
      style={{
        backgroundColor: scrolled ? 'rgba(10, 25, 47, 0.95)' : 'rgba(10, 25, 47, 0.9)',
        backdropFilter: `blur(${scrolled ? '12px' : '8px'})`,
        WebkitBackdropFilter: `blur(${scrolled ? '12px' : '8px'})`,
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <nav className="container mx-auto px-4 sm:px-6 py-4 max-w-full">
        <div className="flex items-center justify-between gap-3 lg:justify-between">
          <motion.a 
            href="#home" 
            className="hidden lg:block text-secondary font-bold text-base sm:text-lg md:text-xl relative group min-w-0 shrink"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="relative z-10 block truncate sm:whitespace-nowrap max-w-[9.5rem] xs:max-w-none">
              {t('nav.brand')}
            </span>
            <div
              className="absolute -inset-2 bg-secondary/10 rounded-lg -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
          </motion.a>

          {/* Mobile: centered brand + language + menu */}
          <div className="flex w-full items-center justify-center gap-3 lg:hidden">
            <motion.a
              href="#home"
              className="text-secondary font-bold text-base relative group min-w-0 shrink text-center"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleMenuClick}
            >
              <span className="relative z-10 block truncate max-w-[8.5rem] sm:max-w-none">
                {t('nav.brand')}
              </span>
              <div className="absolute -inset-2 bg-secondary/10 rounded-lg -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.a>

            <LanguageSwitcher />

            <motion.button
              type="button"
              className="nav-menu-toggle text-textSecondary p-2 relative z-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={t('nav.toggleMenu')}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              whileTap={{ scale: 0.9 }}
            >
              <motion.svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
                animate={isMenuOpen ? 'open' : 'closed'}
              >
                <motion.path
                  variants={{
                    closed: { d: 'M4 6h16M4 12h16M4 18h16' },
                    open: { d: 'M6 18L18 6M6 6l12 12' },
                  }}
                  transition={{ duration: 0.3 }}
                  d={isMenuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </motion.svg>
            </motion.button>
          </div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1 shrink-0">
            {MENU_ITEM_IDS.map((id, index) => {
              const isActive = activeSection === id;
              return (
                <motion.a
                  key={index}
                  href={`#${id}`}
                  className={`relative px-4 py-2 font-medium transition-colors duration-300 whitespace-nowrap ${
                    isActive ? 'text-secondary' : 'text-textSecondary hover:text-secondary'
                  }`}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t(`nav.${id}`)}
                  {isActive && (
                    <motion.div
                      layoutId="activeSection"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-secondary"
                      initial={false}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                </motion.a>
              );
            })}

            <LanguageSwitcher className="ml-2" />

            {/* Upwork Hire Me Button */}
            <motion.a
              href="https://www.upwork.com/freelancers/~017cad0a2a355873b2?mp_source=share"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-xl overflow-hidden ml-4 shrink-0"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Animated background */}
              <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-emerald-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Button content */}
              <div className="relative z-10 flex items-center gap-2">
                <svg className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3.002-2.439-5.453-5.439-5.453z"/>
                </svg>
                <span className="text-sm">{t('nav.hireMe')}</span>
                <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
              </div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
            </motion.a>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden mt-4 bg-primary/95 backdrop-blur-sm rounded-lg border border-secondary/20 overflow-hidden"
            id="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4 flex flex-col items-center text-center">
              {MENU_ITEM_IDS.map((id, index) => (
                <motion.a
                  key={index}
                  href={`#${id}`}
                  className="block w-full text-textSecondary hover:text-secondary transition-colors font-medium py-2 text-lg"
                  onClick={handleMenuClick}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  {t(`nav.${id}`)}
                </motion.a>
              ))}

              {/* Mobile Upwork Button */}
              <motion.a
                href="https://www.upwork.com/freelancers/~017cad0a2a355873b2?mp_source=share"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full max-w-xs mt-4 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold rounded-lg text-center hover:from-green-600 hover:to-emerald-700 transition-all duration-300"
                onClick={handleMenuClick}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: MENU_ITEM_IDS.length * 0.1 + 0.05 }}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.839-3.06 1.492 0 2.703 1.212 2.703 2.703-.001 1.489-1.212 2.702-2.704 2.702zm0-8.14c-2.539 0-4.51 1.649-5.31 4.366-1.22-1.834-2.148-4.036-2.687-5.892H7.828v7.112c-.002 1.406-1.141 2.546-2.547 2.548-1.405-.002-2.543-1.143-2.545-2.548V3.492H0v7.112c0 2.914 2.37 5.303 5.281 5.303 2.913 0 5.283-2.389 5.283-5.303v-1.19c.529 1.107 1.182 2.229 1.974 3.221l-1.673 7.873h2.797l1.213-5.71c1.063.679 2.285 1.109 3.686 1.109 3 0 5.439-2.452 5.439-5.45 0-3.002-2.439-5.453-5.439-5.453z"/>
                  </svg>
                  <span>{t('nav.hireMeOnUpwork')}</span>
                  <div className="w-2 h-2 bg-green-300 rounded-full animate-pulse" />
                </div>
              </motion.a>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

export default Header;
